import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscribeToNewsletter } from '../../services/newsletterService';
import { API_BASE_URL } from '../../config/api';
import SocialIcons from '../SocialIcons';
import './Footer.css';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [socialLinks, setSocialLinks] = useState([]);

  // Fetch social links from API
  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/settings/social-links/public`);
        if (response.ok) {
          const data = await response.json();
          // All returned links are already visible (filtered by API)
          setSocialLinks(data || []);
        }
      } catch (error) {
        console.error('Error fetching social links:', error);
      }
    };
    fetchSocialLinks();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage({ text: 'Please enter your email', type: 'error' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ text: 'Please enter a valid email', type: 'error' });
      return;
    }

    setIsLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await subscribeToNewsletter(email, null, 'footer');
      setMessage({ text: response.message || 'Successfully subscribed!', type: 'success' });
      setEmail('');
    } catch (error) {
      setMessage({ text: error.error || 'Failed to subscribe', type: 'error' });
    } finally {
      setIsLoading(false);
      // Clear message after 5 seconds
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-main-content">
        {/* Footer Columns Section */}
        <div className="footer-columns-container">
          {/* Our Services */}
          <div className="footer-column">
            <h4 className="footer-title">Our Services</h4>
            <ul className="footer-links">
              <li><Link to="/advertise-with-us">Advertise with us</Link></li>
              <li><Link to="/membership-packages">Membership Plan</Link></li>
              <li><Link to="/news">News</Link></li>
            </ul>
          </div>

          {/* Divider */}
          <div className="footer-vertical-divider"></div>

          {/* Suppliers Tools */}
          <div className="footer-column">
            <h4 className="footer-title">Suppliers Tools</h4>
            <ul className="footer-links">
              <li><Link to="/send-inquire">Post your Requirement</Link></li>
              <li><Link to="/documents-submit">Become a Supplier</Link></li>
              <li><Link to="/buy-leads">Latest Buy leads</Link></li>
              <li><Link to="/specialists">Specialists</Link></li>
              <li><Link to="/projects">Projects</Link></li>
            </ul>
          </div>

          {/* Divider */}
          <div className="footer-vertical-divider"></div>

          {/* Quick Links */}
          <div className="footer-column">
            <h4 className="footer-title">Quick Links</h4>
            <ul className="footer-links">
              <li><Link to="/about-us">About Us</Link></li>
              <li><Link to="/contact-us">Contact Us</Link></li>
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms-conditions">Terms & Conditions</Link></li>
            </ul>
            
            {/* Contact Info */}
            <div className="footer-contact-info">
              <div className="contact-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_whatsapp)">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12.7669 2.17987C11.3636 0.774914 9.49725 0.000820312 7.50898 0C3.41201 0 0.0776562 3.33426 0.0760156 7.43233C0.0754531 8.74237 0.417664 10.0212 1.06815 11.1484L0.0136719 15L3.95401 13.9664C5.03972 14.5586 6.26206 14.8707 7.50596 14.8711H7.5091C11.6056 14.8711 14.9403 11.5366 14.9419 7.43829C14.9427 5.45215 14.1703 3.58476 12.7669 2.17987ZM7.50898 13.6158H7.5064C6.3979 13.6153 5.31071 13.3174 4.36203 12.7546L4.13656 12.6207L1.7983 13.2341L2.42239 10.9543L2.27544 10.7206C1.65701 9.73696 1.33044 8.60008 1.331 7.43278C1.33229 4.02656 4.10377 1.25536 7.51147 1.25536C9.1616 1.2559 10.7128 1.89933 11.8792 3.06708C13.0455 4.23483 13.6875 5.78702 13.687 7.43782C13.6855 10.8443 10.9142 13.6158 7.50898 13.6158ZM10.8977 8.98879C10.712 8.89579 9.79889 8.44664 9.6286 8.38457C9.45849 8.32258 9.33453 8.29171 9.21085 8.47757C9.08698 8.66343 8.7311 9.08182 8.62271 9.20568C8.51431 9.32967 8.4061 9.34521 8.22033 9.25221C8.03459 9.15928 7.43618 8.96308 6.72675 8.33036C6.17468 7.83789 5.80198 7.22972 5.69358 7.04388C5.58537 6.85783 5.69265 6.76701 5.77505 6.66482C5.9761 6.41517 6.17743 6.15342 6.23933 6.02955C6.30129 5.90557 6.27026 5.7971 6.22376 5.70417C6.17743 5.61124 5.80601 4.69708 5.65128 4.32511C5.50041 3.96312 5.34741 4.01198 5.23334 4.00633C5.12513 4.00092 5.00126 3.99982 4.8774 3.99982C4.7536 3.99982 4.55236 4.04622 4.38209 4.23227C4.21189 4.41822 3.73217 4.86747 3.73217 5.78163C3.73217 6.69579 4.39765 7.57889 4.49049 7.70285C4.5833 7.82683 5.80015 9.70272 7.66315 10.5071C8.10626 10.6987 8.45215 10.8128 8.72196 10.8984C9.1669 11.0398 9.57167 11.0198 9.89173 10.972C10.2486 10.9186 10.9905 10.5227 11.1454 10.0889C11.3001 9.65505 11.3001 9.28324 11.2536 9.20568C11.2073 9.12825 11.0834 9.08182 10.8977 8.98879Z" fill="#F5F5F5"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_whatsapp">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                <span>009715444586500</span>
              </div>
              <div className="contact-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_email)">
                    <path d="M13.4443 2.7041L13.6211 2.71289C14.0309 2.75417 14.4161 2.93577 14.71 3.22949C15.0456 3.56516 15.2353 4.02042 15.2363 4.49512V11.5049C15.2358 11.9798 15.0468 12.4356 14.7109 12.7715C14.417 13.0654 14.0311 13.2461 13.6211 13.2871L13.4443 13.2959H2.55566C2.0808 13.2954 1.6249 13.1072 1.28906 12.7715C0.953216 12.4356 0.764202 11.9798 0.763672 11.5049V4.49512L0.772461 4.31836C0.813775 3.90857 0.995288 3.52327 1.28906 3.22949C1.62476 2.8938 2.07996 2.70498 2.55469 2.7041H13.4443ZM9.34473 8.94043C8.53937 9.5566 7.46275 9.55637 6.6543 8.94043L2.50195 12.374C2.51974 12.3753 2.53768 12.3799 2.55566 12.3799H13.4434C13.4611 12.3797 13.4785 12.3753 13.4961 12.374L9.34473 8.94043ZM1.71387 4.2666C1.69253 4.34008 1.68066 4.41687 1.68066 4.49609V11.5039L1.68848 11.6201C1.69787 11.6869 1.71664 11.7517 1.74121 11.8145L5.9668 8.32031L1.71387 4.2666ZM10.0322 8.32031L14.2568 11.8145C14.2953 11.7174 14.3193 11.614 14.3193 11.5049V4.49609C14.3193 4.41675 14.3055 4.34015 14.2842 4.2666L10.0322 8.32031ZM2.55469 3.62012C2.49589 3.62013 2.43845 3.62611 2.38281 3.6377L7.08887 8.12402C7.33431 8.35786 7.661 8.48828 8 8.48828C8.33884 8.48817 8.66482 8.35776 8.91016 8.12402L13.6133 3.63867C13.5585 3.62749 13.5022 3.6211 13.4443 3.62109L2.55469 3.62012Z" fill="#F5F5F5" stroke="#F5F5F5" strokeWidth="0.25"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_email">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                <span>Info@gulfconstructor.com</span>
              </div>
            </div>
          </div>

          {/* Newsletter Section */}
          <div className="footer-newsletter-section">
            <div className="newsletter-content">
              <div className="newsletter-text-content">
                <h4 className="newsletter-title">NEWSLETTER</h4>
                <p className="newsletter-description">Subscribe to our new channel to get latest updates</p>
              </div>
              <form className="newsletter-form" onSubmit={handleSubscribe}>
                <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="newsletter-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
                <button 
                  type="submit" 
                  className="newsletter-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
              {message.text && (
                <p className={`newsletter-message ${message.type}`}>
                  {message.text}
                </p>
              )}
            </div>

            {/* App Download Badges */}
            <div className="app-downloads">
              <div className="app-badge">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_playstore)">
                    <path d="M25.18 11.8989L20.4374 9.15967L15.582 14.015L20.437 18.8701L25.1961 16.1217C25.9383 15.6708 26.3811 14.8832 26.3811 14.015C26.3811 13.1469 25.9379 12.3592 25.18 11.8989Z" fill="#FFCE00"/>
                    <path d="M15.5815 14.0155L2.35652 0.695312C2.12215 0.927494 1.93614 1.20383 1.80924 1.50835C1.68233 1.81287 1.61706 2.13953 1.61719 2.46943V25.521C1.61719 26.211 1.89654 26.8352 2.35652 27.2955L15.5815 14.0155Z" fill="url(#paint0_linear_playstore)"/>
                    <path d="M15.5824 14.0166L20.4378 9.16123L20.4455 9.15353L13.9399 5.40786L5.36411 0.367666C4.59187 -0.108769 3.65545 -0.125222 2.86676 0.318307C2.68613 0.433128 2.5216 0.548298 2.35742 0.696024L15.5824 14.0166Z" fill="#00F076"/>
                    <path d="M2.35742 27.2938C2.5216 27.4415 2.68613 27.5567 2.86676 27.6715C3.26093 27.885 3.67191 28.0002 4.08253 28.0002C4.52606 28.0002 4.95314 27.8689 5.34766 27.6389L13.9396 22.6229L20.4452 18.8772L20.4375 18.8695L15.5824 14.0142L2.35742 27.2938Z" fill="#F63448"/>
                  </g>
                  <defs>
                    <linearGradient id="paint0_linear_playstore" x1="-4.29327" y1="20.6454" x2="9.00665" y2="7.34509" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#00E2FF"/>
                      <stop offset="1" stopColor="#00C5FF"/>
                    </linearGradient>
                    <clipPath id="clip0_playstore">
                      <rect width="28" height="28" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                <div className="app-badge-text">
                  <span className="app-badge-small">Get it on</span>
                  <span className="app-badge-large">Google Play</span>
                </div>
              </div>
              
              <div className="app-badge">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21.4643 26.233C20.0169 27.6367 18.4367 27.415 16.9155 26.7501C15.3057 26.0704 13.8288 26.0409 12.1304 26.7501C10.0037 27.6662 8.88122 27.4003 7.6111 26.233C0.403915 18.8007 1.46727 7.48234 9.6492 7.06861C11.643 7.17205 13.0313 8.16203 14.198 8.25069C15.9407 7.89606 17.6096 6.87653 19.4705 7.00951C21.7006 7.18682 23.3842 8.07338 24.4919 9.66917C19.884 12.4323 20.9769 18.5052 25.2008 20.2044C24.359 22.4208 23.2661 24.6224 21.4495 26.2477L21.4643 26.233ZM14.0503 6.97996C13.8288 3.68493 16.5019 0.966162 19.5739 0.700195C20.0022 4.51238 16.118 7.34936 14.0503 6.97996Z" fill="white"/>
                </svg>
                <div className="app-badge-text">
                  <span className="app-badge-small">Available on the</span>
                  <span className="app-badge-large">App Store</span>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="social-section">
              <span className="social-title">Follow Us</span>
              <SocialIcons links={socialLinks} size={36} gap={10} theme="dark" />
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="footer-divider-line"></div>
        <p className="copyright">© 2025 gulf Constuctor. All rights reserved.</p>
      </div>
    </footer>
  );
};

export default Footer;
